import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthContext';
import { type MeResponse } from './client';
import { useApi } from './useApi';

const MeContext = createContext<MeResponse | null>(null);

/**
 * Fetches `/api/me` once per authenticated session and shares the identity
 * with every consumer (account menu label, assistant storage scoping), so
 * mounting another consumer never costs another network request. `null` while
 * loading, unauthenticated, or when the request fails.
 */
export function MeProvider({ children }: { children: ReactNode }) {
  const { status, sessionVersion } = useAuth();
  const api = useApi();
  const [loaded, setLoaded] = useState<{
    api: typeof api;
    sessionVersion: number;
    me: MeResponse;
  } | null>(null);
  const me =
    status === 'authenticated' && loaded?.api === api && loaded.sessionVersion === sessionVersion
      ? loaded.me
      : null;

  useEffect(() => {
    if (status !== 'authenticated') {
      setLoaded(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const value = await api.me();
        if (!cancelled) setLoaded({ api, sessionVersion, me: value });
      } catch {
        if (!cancelled) setLoaded(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, status, sessionVersion]);

  return <MeContext.Provider value={me}>{children}</MeContext.Provider>;
}

/** The shared `/api/me` identity, or null until it resolves. */
export function useMe(): MeResponse | null {
  return useContext(MeContext);
}
